import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { Post } from "@/hooks/useSearch";
import { PostTile } from "./PostTile";

const GAP = 8;

// Column count picked from the container width — not the viewport — so the
// grid stays consistent whether it lives in a sidebar layout or full width.
// Mobile (<480) gets 2 columns; the tile-aspect-ratio rows then hit a tap
// target size that's actually usable.
function pickColumnCount(width: number): number {
  if (width < 480) return 2;
  if (width < 768) return 3;
  if (width < 1280) return 4;
  return 5;
}

// Same "viewport minus chrome" idea as before, but measured against the real
// distance from the grid's top to the bottom of the viewport. Works whether
// the page header is 1 row or wraps to 2 on mobile.
function useFillViewportHeight(ref: React.RefObject<HTMLElement | null>): number {
  const [h, setH] = useState(0);
  useLayoutEffect(() => {
    function measure() {
      const el = ref.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setH(Math.max(200, window.innerHeight - top - 16));
    }
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [ref]);
  return h;
}

interface Props {
  posts: Post[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  isLoading: boolean;
}

export function TileGrid({ posts, hasNextPage, isFetchingNextPage, fetchNextPage, isLoading }: Props) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(() =>
    typeof window === "undefined" ? 4 : pickColumnCount(window.innerWidth),
  );
  const fillHeight = useFillViewportHeight(parentRef);

  // Pick column count from the container the grid actually lives in. Watch
  // it with a ResizeObserver so a sidebar toggle, rotate, or browser resize
  // all flow through.
  useLayoutEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    const update = () => setColumnCount(pickColumnCount(el.clientWidth));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rows: Post[][] = [];
  for (let i = 0; i < posts.length; i += columnCount) {
    rows.push(posts.slice(i, i + columnCount));
  }

  // Without measureElement, the virtualizer trusts estimateSize. Tile heights
  // depend on each post's aspect ratio (padding-bottom hack), and a row's
  // actual height is the tallest tile in it — usually far more than the 240px
  // guess below. That mismatch made subsequent rows overlap their predecessor.
  //
  // measureElement attaches a ResizeObserver to each row container (via the
  // ref we set on the row div) so the virtualizer learns the real heights and
  // offsets following rows correctly. The estimate still matters for the
  // initial paint and for rows that haven't been measured yet.
  const rowVirtualizer = useVirtualizer({
    count: rows.length + (hasNextPage ? 1 : 0),
    getScrollElement: () => parentRef.current,
    estimateSize: () => 240,
    overscan: 3,
    measureElement: (el) => el.getBoundingClientRect().height,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    const last = virtualItems[virtualItems.length - 1];
    if (!last) return;
    if (last.index >= rows.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [virtualItems, rows.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) {
    return (
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}>
        {Array.from({ length: columnCount * 4 }).map((_, i) => (
          <div key={i} className="bg-surface-2 rounded-lg animate-pulse aspect-square" />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-20 text-zinc-500">
        <p className="text-lg">No results found</p>
        <p className="text-sm mt-1">Try different tags or remove some filters</p>
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      className="overflow-auto"
      style={{ height: fillHeight ? `${fillHeight}px` : "calc(100vh - 120px)" }}
    >
      <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: "relative" }}>
        {virtualItems.map((virtualRow) => {
          const row = rows[virtualRow.index];

          if (!row) {
            return (
              <div
                key="loading-sentinel"
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start}px)`,
                  height: `${virtualRow.size}px`,
                }}
                className="flex items-center justify-center text-zinc-600 text-sm"
              >
                {isFetchingNextPage ? "Loading more…" : null}
              </div>
            );
          }

          return (
            <div
              key={virtualRow.index}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
                paddingBottom: `${GAP}px`,
              }}
            >
              <div
                className="grid gap-2"
                style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}
              >
                {row.map((post) => (
                  <PostTile key={post.id} post={post} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
